import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { ChevronDown } from '@/components/ui/icons';
import { Icon, type IconComponent, type IconSize } from './Icon';
import { Chip } from './Chip';
import type { Tooltip as TooltipComponent } from '@/components/ui/primitives/Tooltip';

/*
 * An icon Button's hint is the one Tooltip, but the Tooltip (floating-ui) stays out of the first-paint bundle
 * (scripts/check-bundle-size.mjs "non-viewer initial", 150 kB gzip; a static import put it at 162 kB). It loads
 * right after the entry; an icon Button rendered before then gets its hint from the next render on.
 */
let loadedTooltip: typeof TooltipComponent | null = null;
const tooltipListeners = new Set<() => void>();
/** Resolves once the Tooltip has loaded (tests that hover an icon Button await it). */
export const iconHintReady: Promise<void> = import('@/components/ui/primitives/Tooltip').then(
  (m) => {
    loadedTooltip = m.Tooltip;
    tooltipListeners.forEach((l) => l());
  },
  () => undefined, // a failed chunk load leaves icon Buttons without a hint; their name (aria-label) stays
);
const subscribeTooltip = (l: () => void) => {
  tooltipListeners.add(l);
  return () => {
    tooltipListeners.delete(l);
  };
};
const getTooltip = () => loadedTooltip;

/** The icon Button's hint: its child inside the one Tooltip once that has loaded. */
function IconHint({ hint, children }: { hint: string; children: ReactElement }) {
  const Tooltip = useSyncExternalStore(subscribeTooltip, getTooltip, getTooltip);
  return Tooltip ? <Tooltip label={hint}>{children}</Tooltip> : children;
}

/** The Broadsheet kit's button variants (packages/ui-kit/base.css `.bk-button--*`). */
export type ButtonVariant = 'default' | 'primary' | 'ghost' | 'alert';
export type ButtonSize = 'default' | 'small' | 'lg' | 'touch';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** default = bordered panel, primary = filled, ghost = chromeless (toolbars, quiet actions), alert = destructive. */
  variant?: ButtonVariant;
  /** Kit sizes sm / md / lg (--bk-control-*: 24/28/34 px, 44/44/48 on a coarse pointer). `touch` is pmt's one
   *  addition: at least --bk-touch-min (44 px) on every pointer, for the controls a Pencil (a fine pointer) uses on
   *  the sheet and in the rail, which were 44 px before S2. */
  size?: ButtonSize;
  /** Busy with its own action (the one loading button): the dots lead the label, the variant colour stays (no
   *  disabled fade), aria-busy="true", and clicks / Enter / Space do nothing until it settles (no double submit).
   *  The width never shrinks while busy. Pass the surface's own pending words as children ("Saving..."). Wins over
   *  `disabled`: never repeat the pending flag there. */
  loading?: boolean;
  children?: ReactNode;
  /** When set, renders as an anchor (`<a href>`) styled as a button. */
  href?: string;

  /* --- Icon / toggle / split capabilities (folded in from the Chip and
     IconButton atoms) --- */
  /** Leading (or, with no children, sole) drafting-icon glyph. */
  icon?: IconComponent;
  /** Icon px size (defaults from `size`). */
  iconSize?: number;
  /** Toggle/pressed state -- when defined the button is a toggle (aria-pressed); the kit fills a pressed button
   *  to ink. */
  active?: boolean;
  /** Trailing tally, e.g. "4/7". */
  count?: string;
  /** Disclosure state for the split expand affordance. */
  expanded?: boolean;
  /** When set, appends a chevron that opens a popover (a split control). */
  onExpand?: () => void;
  /** The content is a picture (a colour swatch, a thumbnail), not a label: the icon-only shape -- no padding, at
   *  least as wide as it is tall. */
  square?: boolean;
  /** Icon-only: the hint shown in the Tooltip (defaults to `title`, then `aria-label`). An icon-only Button never
   *  renders a native `title` -- the Tooltip is the one hint (spec--pmt-ui-redesign 5(b)). */
  tooltip?: string;
}

/** A busy Button swallows the click (and so the form submit / Enter / Space it would trigger). */
function swallowClick(e: MouseEvent<HTMLElement>) {
  e.preventDefault();
}

/** Holds the idle width as the busy Button's min-width, so swapping in the dots (and a pending label) never shrinks it
 *  under the finger. Set on the element in a layout effect (before paint, no re-render); returns the ref to attach. */
function useStableBusyWidth(loading: boolean | undefined, forwarded: Ref<HTMLButtonElement>) {
  const el = useRef<HTMLButtonElement | null>(null);
  const idleWidth = useRef(0);
  // only a Button that takes a `loading` prop measures (a few dialog / card actions, not every toolbar control)
  const tracks = loading !== undefined;
  useLayoutEffect(() => {
    if (tracks && !loading && el.current) idleWidth.current = el.current.offsetWidth;
  });
  useLayoutEffect(() => {
    const node = el.current;
    if (!tracks || !loading || !node || idleWidth.current <= 0) return;
    const before = node.style.minWidth;
    node.style.minWidth = `${idleWidth.current}px`;
    return () => {
      node.style.minWidth = before;
    };
  }, [tracks, loading]);
  const ref = useCallback(
    (node: HTMLButtonElement | null) => {
      el.current = node;
      if (typeof forwarded === 'function') forwarded(node);
      else if (forwarded) (forwarded as { current: HTMLButtonElement | null }).current = node;
    },
    [forwarded],
  );
  return ref;
}

const KIT_SIZE: Record<ButtonSize, string> = { default: '', small: 'bk-button--sm', lg: 'bk-button--lg', touch: 'button--touch' };

/** The kit classes for a variant + size (spec--pmt-ui-redesign 5(b)): `bk-button`, `bk-button--<variant>`, size. */
function buttonClasses(variant: ButtonVariant, size: ButtonSize = 'default'): string {
  return ['bk-button', variant !== 'default' ? `bk-button--${variant}` : '', KIT_SIZE[size]].filter(Boolean).join(' ');
}

/**
 * The single interactive control primitive. It emits the Broadsheet kit's `bk-button` classes and variant names
 * (packages/ui-kit/base.css); Button.css only points the kit's colour tokens at the current theme. Renders as:
 * - a normal button / anchor (text, optional leading `icon`),
 * - an icon-only square button when `icon` is set with no children (former
 *   IconButton -- ref-forwarded so overlays can anchor to it); ghost unless a variant says otherwise,
 * - a toggle with `active` (aria-pressed), and a split control with `onExpand`.
 * Extra `className`s are hooks (e2e selectors, layout), never a look of their own (source scan: button-override).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'default',
    loading: loadingProp,
    children,
    className = '',
    disabled,
    href,
    icon,
    iconSize,
    active,
    count,
    expanded,
    onExpand,
    square,
    tooltip,
    onClick,
    ...props
  },
  ref
) {
  const isToggle = active !== undefined;
  const loading = !!loadingProp;
  const busyRef = useStableBusyWidth(loadingProp, ref);
  const iconGlyphSize: IconSize = iconSize !== undefined ? (iconSize <= 17 ? 16 : 20) : size === 'small' ? 16 : 20;

  // Icon-only square button (former IconButton). Reuses the .icon-button chrome
  // so its states (is-active, is-sticky, is-flashing, ...) keep working.
  if (icon != null && children == null && count == null && onExpand === undefined && href === undefined) {
    // the name stays (aria-label, or the old title when that was the only name); the hint moves to the Tooltip
    const { title, ...rest } = props;
    const label = (props as { 'aria-label'?: string })['aria-label'] ?? title;
    const hint = tooltip ?? title ?? label;
    const classes = [
      buttonClasses(variant === 'default' ? 'ghost' : variant, size),
      'icon-button',
      active ? 'is-active' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const iconButton = (
      <button
        ref={ref}
        type="button"
        {...rest}
        aria-label={label}
        className={classes}
        disabled={disabled}
        aria-pressed={isToggle ? active : undefined}
        onClick={onClick}
      >
        <Icon icon={icon} size={iconGlyphSize} />
      </button>
    );
    return hint ? <IconHint hint={hint}>{iconButton}</IconHint> : iconButton;
  }

  const body = (
    <>
      {icon && <Icon icon={icon} size={16} />}
      {children != null && <span className="button__label">{children}</span>}
      {count != null && <Chip count={count} />}
    </>
  );
  const spinnerSize = size === 'small' ? 12 : size === 'lg' ? 16 : 14;
  const content = loading ? (
    <span className="button__label button__label--loading">
      <LoadingSpinner size={spinnerSize} />
      {children}
    </span>
  ) : icon || count ? (
    body
  ) : (
    children
  );

  // Split control: main button + disclosure chevron.
  if (onExpand !== undefined) {
    return (
      <span
        className={['button-split', active ? 'is-active' : '', className]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          {...props}
          ref={busyRef}
          type="button"
          className={`${buttonClasses('ghost', size)} button-split__main`}
          disabled={disabled && !loading}
          aria-busy={loading || undefined}
          aria-pressed={isToggle ? active : undefined}
          aria-expanded={!onClick ? expanded : undefined}
          onClick={loading ? swallowClick : (onClick ?? onExpand)}
        >
          {content}
        </button>
        <button
          type="button"
          className={`${buttonClasses('ghost', size)} icon-button button-split__expand`}
          aria-label={`${typeof children === 'string' ? children : 'More'} options`}
          aria-expanded={expanded}
          disabled={disabled || loading}
          onClick={onExpand}
        >
          <ChevronDown size={13} />
        </button>
      </span>
    );
  }

  const classes = [
    buttonClasses(variant, size),
    square ? 'icon-button' : '',
    active ? 'is-active' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Anchor mode: a link styled as a button.
  if (href !== undefined) {
    const anchorProps = props as unknown as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        ref={ref as unknown as React.Ref<HTMLAnchorElement>}
        href={href}
        className={classes}
        {...anchorProps}
        aria-busy={loading || undefined}
        // onClick is destructured above for the button paths; an anchor needs it too
        onClick={(loading ? swallowClick : onClick) as unknown as AnchorHTMLAttributes<HTMLAnchorElement>['onClick']}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      // a Button never submits a form by accident: pass type="submit" to submit
      type="button"
      {...props}
      ref={busyRef}
      className={classes}
      // busy is not disabled: it keeps its colour (the kit fades :disabled) and says aria-busy; the click is swallowed
      disabled={disabled && !loading}
      aria-busy={loading || undefined}
      aria-pressed={isToggle ? active : undefined}
      onClick={loading ? swallowClick : onClick}
    >
      {content}
    </button>
  );
});
